/**
 * Full-screen chrome for the screens shown before the app shell (sign-in, invite/reset link,
 * onboarding). These render outside the router, so nothing here may use react-router hooks.
 */
import { Languages } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { Lang } from '../../../shared/dto/auth';
import { Wordmark } from '../../components/brand';
import { cx } from '../../components/ui';
import { useI18n } from '../../i18n';

/** Two-button language switch (Íslenska / English). Pre-login choice is kept in localStorage. */
export function LangToggle({ value, onChange }: { value?: Lang; onChange?: (l: Lang) => void }) {
  const { lang, setLang, t } = useI18n();
  const current = value ?? lang;
  const pick = (l: Lang) => (onChange ? onChange(l) : setLang(l));
  const opts: { v: Lang; label: string }[] = [
    { v: 'is', label: t('auth.lang.is') },
    { v: 'en', label: t('auth.lang.en') },
  ];
  return (
    <div
      role="group"
      aria-label={t('auth.lang.label')}
      className="inline-flex items-center gap-1 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-1 shadow-[var(--shadow-card)]"
    >
      <Languages className="mx-1.5 size-5 text-[var(--muted)]" aria-hidden />
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          lang={o.v}
          aria-pressed={current === o.v}
          onClick={() => pick(o.v)}
          className={cx(
            'min-h-11 rounded-xl px-3.5 text-[16px] font-semibold transition',
            current === o.v ? 'bg-[var(--accent)] text-[var(--accent-ink)]' : 'text-[var(--ink-2)] hover:bg-[var(--accent-soft)]',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** The choir's original logo on a white plate (it has a white background); hidden if it can't load (offline). */
export function OriginalLogo({ className }: { className?: string }) {
  const { t } = useI18n();
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <span className={cx('inline-flex rounded-xl bg-white px-3 py-2 shadow-[var(--shadow-card)]', className)}>
      <img
        src="/brand/logo-original.png"
        alt={t('auth.logo_alt')}
        width={478}
        height={136}
        decoding="async"
        className="h-9 w-auto"
        onError={() => setFailed(true)}
      />
    </span>
  );
}

/** Brand block at the top of auth cards. */
export function AuthBrand() {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <Wordmark className="scale-110" />
      <p className="text-[15px] text-[var(--muted)]">{t('auth.tagline')}</p>
    </div>
  );
}

/**
 * Scrollable full-screen page with safe-area padding, the language toggle at the top right
 * (optional `topLeft` slot) and centred content.
 */
export function AuthLayout({
  children,
  wide,
  topLeft,
  hideLang,
  footer,
}: {
  children: ReactNode;
  wide?: boolean;
  topLeft?: ReactNode;
  hideLang?: boolean;
  footer?: ReactNode;
}) {
  return (
    <div
      className="scroll-y flex h-dvh w-full flex-col"
      style={{
        paddingTop: 'var(--top-inset)',
        paddingBottom: 'var(--safe-bottom)',
        paddingLeft: 'var(--safe-left)',
        paddingRight: 'var(--safe-right)',
      }}
    >
      <div className="flex min-h-16 items-center justify-between gap-3 px-4 pt-3 md:px-6">
        <div className="min-w-0">{topLeft}</div>
        {hideLang ? null : <LangToggle />}
      </div>
      <main className="flex flex-1 flex-col items-center justify-center px-4 py-6 md:px-6">
        <div className={cx('w-full', wide ? 'max-w-2xl' : 'max-w-md')}>{children}</div>
      </main>
      {footer ? <div className="flex justify-center px-4 pb-6">{footer}</div> : null}
    </div>
  );
}
